import React, { createContext, useContext, useMemo, useState } from 'react';
import type { AdminAuthContextValue, AdminAuthState } from './AdminAuthTypes';

const STORAGE_KEY = 'njj_admin_auth_v1';

function loadInitialState(): AdminAuthState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { token: null, email: null, name: null };
    const parsed = JSON.parse(raw) as Partial<AdminAuthState>;
    return {
      token: typeof parsed.token === 'string' ? parsed.token : null,
      email: typeof parsed.email === 'string' ? parsed.email : null,
      name: typeof parsed.name === 'string' ? parsed.name : null,
    };
  } catch {
    return { token: null, email: null, name: null };
  }
}

const AdminAuthContext = createContext<AdminAuthContextValue | undefined>(undefined);

export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<AdminAuthState>(() => loadInitialState());

  const value = useMemo<AdminAuthContextValue>(() => {
    const setSession = (data: { token: string; email: string; name: string }) => {
      const next = { token: data.token, email: data.email, name: data.name };
      setState(next);
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // ignore
      }
    };

    const logout = () => {
      const next = { token: null, email: null, name: null };
      setState(next);
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        // ignore
      }
    };

    return { ...state, setSession, logout };
  }, [state]);

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
};

export const useAdminAuth = () => {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider');
  return ctx;
};
